import React, { createContext, useContext, useLayoutEffect, useMemo, useState } from 'react';

/**
 * Options a page can set on the existing site header in App.tsx.
 *
 * WHY: challenge arms C and D (Rob, 2026-10-20) are single-job registration
 * pages. The 2026-10-21 research found about 1 in 15 visitors clicked a header
 * menu link (Marketplace, Pricing, AI Agent, Log in) and left. Those arms ask
 * the header to hide its menu links; the logo always stays. Every other page
 * and arm keeps the header exactly as it was.
 *
 * App.tsx wraps the app in SiteHeaderOptionsProvider and its header reads
 * useSiteHeaderMenuLinksHidden. A page calls useHideSiteHeaderMenuLinks.
 * Requests are counted so two pages asking at once cannot unhide each other,
 * and leaving the page always restores the links.
 */
export type SiteHeaderOptionsApi = {
  /** Hides the menu links until the returned release function is called. */
  hideMenuLinks: () => () => void;
};

export const SiteHeaderOptionsContext = createContext<SiteHeaderOptionsApi | null>(null);

export type SiteHeaderOptionsController = {
  menuLinksHidden: boolean;
  api: SiteHeaderOptionsApi;
};

export const useSiteHeaderOptionsController = (): SiteHeaderOptionsController => {
  const [hideRequests, setHideRequests] = useState(0);
  const api = useMemo<SiteHeaderOptionsApi>(
    () => ({
      hideMenuLinks: () => {
        setHideRequests((count) => count + 1);
        let released = false;
        return () => {
          if (released) return;
          released = true;
          setHideRequests((count) => Math.max(0, count - 1));
        };
      },
    }),
    [],
  );
  return { menuLinksHidden: hideRequests > 0, api };
};

/** While `hidden` is true and this component is mounted, the header shows only its logo. */
export const useHideSiteHeaderMenuLinks = (hidden: boolean): void => {
  const api = useContext(SiteHeaderOptionsContext);
  // Layout effect: the change lands before paint, so the links do not flash.
  useLayoutEffect(() => {
    if (!hidden || !api) return undefined;
    return api.hideMenuLinks();
  }, [hidden, api]);
};

const MenuLinksHiddenContext = createContext(false);

/** Owns the header options for everything below it (App.tsx wraps the app). */
export const SiteHeaderOptionsProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement => {
  const { menuLinksHidden, api } = useSiteHeaderOptionsController();
  return (
    <SiteHeaderOptionsContext.Provider value={api}>
      <MenuLinksHiddenContext.Provider value={menuLinksHidden}>
        {children}
      </MenuLinksHiddenContext.Provider>
    </SiteHeaderOptionsContext.Provider>
  );
};

/** True while some page has asked the header to show only its logo. */
export const useSiteHeaderMenuLinksHidden = (): boolean => useContext(MenuLinksHiddenContext);
